import React, { useCallback } from 'react';
import { PropTypes } from 'prop-types';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { first, get, isEmpty } from 'underscore';
import { getDomain } from '@bingads-webui/url-util';

const getSummaryStyle = () => ({
  advancedUrlOptionsSummaryContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
});

const TARGET_TYPE = {
  Url: 'URL',
  PageTitle: 'PAGE_TITLE',
  PageContent: 'PAGE_CONTENT',
  Category: 'CATEGORY',
  CustomLabel: 'CUSTOM_LABEL',
};

const extractTargetsFromViewModel = (assetGroupUrlRules) => {
  const targetMapping = {
    [TARGET_TYPE.Url]: [],
    [TARGET_TYPE.PageTitle]: [],
    [TARGET_TYPE.PageContent]: [],
    [TARGET_TYPE.Category]: [],
    [TARGET_TYPE.CustomLabel]: [],
  };

  assetGroupUrlRules.forEach((rule) => {
    const type = get(rule, 'TargetCondition1', '');
    const values = [get(rule, 'TargetValue1', ''), get(rule, 'TargetValue2', ''), get(rule, 'TargetValue3', '')]
      .filter(Boolean);

    if (targetMapping[type]) {
      targetMapping[type].push(values);
    }
  });

  return {
    urlTargets: targetMapping[TARGET_TYPE.Url],
    pageTitleTargets: targetMapping[TARGET_TYPE.PageTitle],
    pageContentTargets: targetMapping[TARGET_TYPE.PageContent],
    categoryTargets: targetMapping[TARGET_TYPE.Category],
    customLabelTargets: targetMapping[TARGET_TYPE.CustomLabel],
  };
};

export const UrlOptionsDetailsWithRules = ({
  i18n, finalUrls, path1, path2, mobileFinalUrls, urlRules,
}) => {
  const styles = useDefaultStyles(getSummaryStyle);

  const finalUrl = first(finalUrls);
  const mobileFinalUrl = first(mobileFinalUrls);
  let displayUrl = '';
  if (!isEmpty(path1)) {
    const finalUrlDomain = getDomain(finalUrl) || 'www.example.com';
    displayUrl = `${finalUrlDomain}/${path1}`;
    if (!isEmpty(path2)) {
      displayUrl = `${displayUrl}/${path2}`;
    }
  }
  const noDisplayUrl = isEmpty(displayUrl);
  const noMboileUrl = isEmpty(mobileFinalUrl);
  const noUrlRules = isEmpty(urlRules);
  const noSettingsApplied = noDisplayUrl && noMboileUrl && noUrlRules;

  let urlPathText = '';
  if (noSettingsApplied) {
    urlPathText = i18n.getString(_TL_('No URL settings applied'));
  } else {
    if (!noDisplayUrl) {
      urlPathText += `${i18n.getString(_TL_('Display URL'))}: ${displayUrl}`;
    }
    if (!noMboileUrl) {
      if (urlPathText.length > 0) {
        urlPathText += '; '; // Add separator if there are details before
      }
      urlPathText += `${i18n.getString(_TL_('Mobile URL'))}: ${mobileFinalUrl}`;
    }
  }

  const {
    urlTargets, pageTitleTargets, pageContentTargets, categoryTargets, customLabelTargets,
  } = extractTargetsFromViewModel(urlRules || []);
  const renderRulesSummary = useCallback(() => {
    if (urlRules.length === 0) {
      return (<span>{i18n.getString(_TL_('No URL rules set'))}</span>);
    }
    return (
      <>
        {urlTargets.length > 0 && <span>{i18n.getString(_TL_('URL contains'))}: {urlTargets.map(target => target.join(', ')).join('; ')}; </span>}
        {pageTitleTargets.length > 0 && <span>{i18n.getString(_TL_('Page title is'))}: {pageTitleTargets.map(target => target.join(', ')).join('; ')}; </span>}
        {pageContentTargets.length > 0 && <span>{i18n.getString(_TL_('Page content contains'))}: {pageContentTargets.map(target => target.join(', ')).join('; ')}; </span>}
        {categoryTargets.length > 0 && <span>{i18n.getString(_TL_('Category is'))}: {categoryTargets.map(target => target.join(', ')).join('; ')}; </span>}
        {customLabelTargets.length > 0 && <span>{i18n.getString(_TL_('Custom label is'))}: {customLabelTargets.map(target => target.join(', ')).join('; ')}; </span>}
      </>
    );
  }, [urlRules, i18n, urlTargets, pageTitleTargets, pageContentTargets, categoryTargets, customLabelTargets]);

  return (
    <div className={styles.advancedUrlOptionsSummaryContainer}>
      <span>{urlPathText}</span>
      { !noSettingsApplied && renderRulesSummary() }
    </div>
  );
};

UrlOptionsDetailsWithRules.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  finalUrls: PropTypes.arrayOf(PropTypes.string),
  path1: PropTypes.string,
  path2: PropTypes.string,
  mobileFinalUrls: PropTypes.arrayOf(PropTypes.string),
  urlRules: PropTypes.arrayOf(PropTypes.shape({})),
};

UrlOptionsDetailsWithRules.defaultProps = {
  finalUrls: [],
  path1: '',
  path2: '',
  mobileFinalUrls: [],
  urlRules: [],
};
